import { z } from 'zod';
// --- Universal style tokens ---
const spacingTokenEnum = z.enum(['none', 'xs', 'sm', 'md', 'lg', 'xl']);
export const componentStyleSchema = z.strictObject({
    bg: z.enum(['surface', 'surface-raised', 'primary', 'accent', 'muted', 'transparent']).optional(),
    color: z
        .enum(['foreground', 'muted', 'primary', 'accent', 'success', 'warning', 'danger'])
        .optional(),
    padding: spacingTokenEnum.optional(),
    margin: spacingTokenEnum.optional(),
    gap: z.union([spacingTokenEnum, z.number()]).optional(),
    radius: z.enum(['none', 'sm', 'md', 'lg', 'full']).optional(),
    border: z.enum(['none', 'default', 'muted', 'accent']).optional(),
    shadow: z.enum(['none', 'sm', 'md', 'lg']).optional(),
    width: z.union([z.number(), z.enum(['full', 'auto', 'half'])]).optional(),
    height: z.union([z.number(), z.enum(['full', 'auto'])]).optional(),
    minHeight: z.number().optional(),
    opacity: z.number().min(0).max(1).optional(),
});
// --- Tier groupings ---
export const LAYOUT_COMPONENTS = ['Row', 'Column', 'Divider', 'Tabs', 'Accordion'];
export const CONTENT_COMPONENTS = [
    'Text',
    'Metric',
    'Badge',
    'Icon',
    'Mermaid',
    'Image',
    'Progress',
    'Callout',
    'Link',
    'Code',
];
export const DATA_BACKED_COMPONENTS = [
    'BarChart',
    'LineChart',
    'AreaChart',
    'PieChart',
    'ScatterPlot',
    'Heatmap',
    'Histogram',
    'RadarChart',
    'SankeyChart',
    'FunnelChart',
    'GaugeChart',
    'DataTable',
    'Kanban',
    'List',
    'Sparkline',
];
export const DIAGRAM_COMPONENTS = [];
export const INTERACTIVE_COMPONENTS = [
    'Button',
    'Checkbox',
    'Toggle',
    'Select',
    'TextField',
    'Slider',
];
export const DOMAIN_COMPONENTS = ['Calendar', 'Timeline'];
export const ALL_COMPONENT_TYPES = [
    ...LAYOUT_COMPONENTS,
    ...CONTENT_COMPONENTS,
    ...DATA_BACKED_COMPONENTS,
    ...DIAGRAM_COMPONENTS,
    ...INTERACTIVE_COMPONENTS,
    ...DOMAIN_COMPONENTS,
];
// --- Layout schemas ---
// CSS Grid track value: a number with fr/px/%, or `auto`, or a minmax(...) expression.
// Validated per-entry; cross-entry length check happens in validateSurfaceComponents.
const trackValueRegex = /^(\d+(\.\d+)?(fr|px|%)|auto|minmax\([^)]+\))$/;
const tracksSchema = z.array(z.string().regex(trackValueRegex)).optional();
export const rowPropsSchema = z.looseObject({
    align: z.string().optional(),
    justify: z.string().optional(),
    gap: z.number().optional(),
    tracks: tracksSchema,
    style: componentStyleSchema.optional(),
});
export const columnPropsSchema = z.looseObject({
    align: z.string().optional(),
    justify: z.string().optional(),
    gap: z.number().optional(),
    tracks: tracksSchema,
    style: componentStyleSchema.optional(),
});
export const dividerPropsSchema = z.looseObject({
    orientation: z.enum(['horizontal', 'vertical']).optional(),
    style: componentStyleSchema.optional(),
});
export const tabsPropsSchema = z.looseObject({
    tabs: z.array(z.object({ id: z.string(), label: z.string() })).min(1),
    defaultTab: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const accordionPropsSchema = z.looseObject({
    sections: z.array(z.object({ id: z.string(), label: z.string() })).min(1),
    defaultOpen: z.array(z.string()).optional(),
    allowMultiple: z.boolean().optional(),
    style: componentStyleSchema.optional(),
});
// --- Content schemas ---
export const textPropsSchema = z.looseObject({
    text: z.string(),
    variant: z.enum(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body', 'caption']).optional(),
    style: componentStyleSchema.optional(),
});
export const metricPropsSchema = z.looseObject({
    value: z.union([z.string(), z.number()]),
    label: z.string().optional(),
    trend: z.string().optional(),
    trendDirection: z.enum(['up', 'down', 'flat']).optional(),
    trendSentiment: z.enum(['positive', 'negative', 'neutral']).optional(),
    style: componentStyleSchema.optional(),
});
export const badgePropsSchema = z.looseObject({
    text: z.string(),
    color: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const iconPropsSchema = z.looseObject({
    name: z.string(),
    color: z.string().optional(),
    size: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const mermaidPropsSchema = z.looseObject({
    code: z.string(),
    mermaidStyle: z.record(z.string(), z.unknown()).optional(), // renamed from style
    minimized: z.boolean().optional(),
    displayConfig: z.record(z.string(), z.unknown()).optional(),
    annotations: z.array(z.record(z.string(), z.unknown())).optional(),
    style: componentStyleSchema.optional(),
});
export const imagePropsSchema = z.looseObject({
    src: z.string(),
    alt: z.string().optional(),
    fit: z.enum(['contain', 'cover', 'fill', 'none']).optional(),
    style: componentStyleSchema.optional(),
});
export const progressPropsSchema = z.looseObject({
    value: z.number().min(0).max(100),
    label: z.string().optional(),
    color: z.string().optional(),
    variant: z.enum(['bar', 'circle']).optional(),
    style: componentStyleSchema.optional(),
});
export const calloutPropsSchema = z.looseObject({
    text: z.string(),
    variant: z.enum(['info', 'warning', 'success', 'error']).optional(),
    icon: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const linkPropsSchema = z.looseObject({
    text: z.string(),
    href: z.url().refine((url) => url.startsWith('https://'), {
        message: 'Only https URLs are allowed',
    }),
    external: z.boolean().optional(),
    style: componentStyleSchema.optional(),
});
export const codePropsSchema = z.looseObject({
    code: z.string(),
    language: z.string().optional(),
    showLineNumbers: z.boolean().optional(),
    style: componentStyleSchema.optional(),
});
// --- Data-backed schemas ---
//
// Chart-variant schemas (BarChart, LineChart, …) live in per-viz files under
// ./visualizations/ as of Task 3 of Plan 7 — the `Chart` meta-type has been
// dissolved into 11 first-class catalog entries. The discriminated `chartPropsSchema`
// and per-variant schemas previously here are gone; each variant's `propsSchema`
// is defined inline in its visualization definition file.
export const dataTablePropsSchema = z.looseObject({
    sourceDataset: z.string(),
    mapping: z
        .object({
        visibleColumns: z.array(z.string()).optional(),
        sortColumn: z.string().optional(),
        sortDirection: z.enum(['asc', 'desc']).optional(),
    })
        .optional(),
    editable: z.boolean().optional(),
    queryConfig: z.record(z.string(), z.unknown()).optional(),
    minimized: z.boolean().optional(),
    displayConfig: z.record(z.string(), z.unknown()).optional(),
    annotations: z.array(z.record(z.string(), z.unknown())).optional(),
    style: componentStyleSchema.optional(),
});
export const kanbanPropsSchema = z.looseObject({
    sourceDataset: z.string(),
    mapping: z.object({
        card: z.string(),
        column: z.string(),
        subtitle: z.string().optional(),
    }),
    queryConfig: z.record(z.string(), z.unknown()).optional(),
    minimized: z.boolean().optional(),
    displayConfig: z.record(z.string(), z.unknown()).optional(),
    annotations: z.array(z.record(z.string(), z.unknown())).optional(),
    style: componentStyleSchema.optional(),
});
export const listPropsSchema = z.looseObject({
    sourceDataset: z.string(),
    template: z.string(),
    queryConfig: z.record(z.string(), z.unknown()).optional(),
    style: componentStyleSchema.optional(),
});
export const sparklinePropsSchema = z.looseObject({
    sourceDataset: z.string(),
    mapping: z.object({
        value: z.string(),
    }),
    type: z.enum(['line', 'bar']).optional(),
    color: z.string().optional(),
    style: componentStyleSchema.optional(),
});
// --- Interactive schemas ---
export const buttonPropsSchema = z.looseObject({
    label: z.string(),
    variant: z.enum(['primary', 'secondary', 'ghost']).optional(),
    icon: z.string().optional(),
    disabled: z.boolean().optional(),
    style: componentStyleSchema.optional(),
});
export const checkboxPropsSchema = z.looseObject({
    checked: z.boolean().optional(),
    label: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const togglePropsSchema = z.looseObject({
    checked: z.boolean().optional(),
    label: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const selectPropsSchema = z.looseObject({
    options: z.array(z.object({ label: z.string(), value: z.string() })).min(1),
    selected: z.string().optional(),
    placeholder: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const textFieldPropsSchema = z.looseObject({
    value: z.string().optional(),
    placeholder: z.string().optional(),
    label: z.string().optional(),
    style: componentStyleSchema.optional(),
});
export const sliderPropsSchema = z.looseObject({
    min: z.number(),
    max: z.number(),
    value: z.number().optional(),
    step: z.number().optional(),
    label: z.string().optional(),
    style: componentStyleSchema.optional(),
});
// --- Domain schemas ---
export const calendarPropsSchema = z.looseObject({
    sourceDataset: z.string(),
    mapping: z.object({
        date: z.string(),
        title: z.string(),
        color: z.string().optional(),
        endDate: z.string().optional(),
    }),
    view: z.enum(['month', 'week']).optional(),
    style: componentStyleSchema.optional(),
});
export const timelinePropsSchema = z.looseObject({
    sourceDataset: z.string(),
    mapping: z.object({
        date: z.string(),
        label: z.string(),
        description: z.string().optional(),
        icon: z.string().optional(),
    }),
    orientation: z.enum(['horizontal', 'vertical']).optional(),
    style: componentStyleSchema.optional(),
});
// --- Registry ---
//
// `catalogSchemas` and `getCatalogSchema` are derived from `ALL_VISUALIZATIONS`
// and live in ./visualizations/index.ts. They are intentionally NOT re-exported
// from this file to avoid a circular import (per-viz files in ./visualizations/
// import the named schema consts above from this module). Consumers should
// import `catalogSchemas` / `getCatalogSchema` from `@deckspace/catalog`
// (the package barrel) or directly from `./visualizations/index.js`.
/**
 * Required props for data-backed component types.
 * Used by contract tests to verify tool descriptions stay in sync.
 * Keys are component type names, values are arrays of required prop names
 * that MUST appear in the tool description.
 */
export const REQUIRED_PROPS = new Map([
    ['BarChart', ['sourceDataset', 'mapping']],
    ['LineChart', ['sourceDataset', 'mapping']],
    ['AreaChart', ['sourceDataset', 'mapping']],
    ['PieChart', ['sourceDataset', 'mapping']],
    ['ScatterPlot', ['sourceDataset', 'mapping']],
    ['Heatmap', ['sourceDataset', 'mapping']],
    ['Histogram', ['sourceDataset', 'mapping']],
    ['RadarChart', ['sourceDataset', 'mapping']],
    ['SankeyChart', ['sourceDataset', 'mapping']],
    ['FunnelChart', ['sourceDataset', 'mapping']],
    ['GaugeChart', ['sourceDataset', 'mapping']],
    ['DataTable', ['sourceDataset']],
    ['Kanban', ['sourceDataset', 'mapping']],
    ['Sparkline', ['sourceDataset', 'mapping']],
    ['List', ['sourceDataset', 'template']],
    ['Calendar', ['sourceDataset', 'mapping']],
    ['Timeline', ['sourceDataset', 'mapping']],
]);
//# sourceMappingURL=component-catalog.js.map